import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency } from "@/lib/utils";
import { DollarSign, Package, ShoppingCart, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";
import {
	Area,
	AreaChart,
	Bar,
	BarChart,
	CartesianGrid,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

interface Product {
	id: string;
	name: string;
	price: number;
	image_url?: string;
	total_views: number;
	total_ordered: number;
}

interface Order {
	id: string;
	created_at: string;
	amount: number;
	customer_name: string;
	status: string;
}

export function SupplierDashboardHome() {
	const { user } = useAuth();
	const [isLoading, setIsLoading] = useState(true);
	const [stats, setStats] = useState({
		totalOrders: 0,
		totalEarnings: 0,
		recentOrders: [] as Order[],
		mostViewedProducts: [] as Product[],
		topSellingProducts: [] as Product[],
		monthlySales: [] as { name: string; value: number }[],
	});

	useEffect(() => {
		async function fetchDashboardData() {
			if (!user) return;

			setIsLoading(true);
			try {
				// Fetch total orders and earnings
				const { data: orderData, error: orderError } = await supabase
					.from("orders")
					.select("id, amount, status")
					.eq("supplier_id", user.id)
					.neq("status", "cancelled");

				if (orderError) throw orderError;

				const totalOrders = orderData?.length || 0;
				const totalEarnings =
					orderData?.reduce(
						(sum, order) =>
							sum + parseFloat(order.amount.toString()),
						0
					) || 0;

				// Fetch recent orders (last 3)
				const { data: recentOrdersData, error: recentOrdersError } =
					await supabase
						.from("orders")
						.select("id, created_at, amount, status, user_id")
						.eq("supplier_id", user.id)
						.order("created_at", { ascending: false })
						.limit(3);

				if (recentOrdersError) throw recentOrdersError;

				// Fetch customer names separately
				const recentOrders = [];
				if (recentOrdersData) {
					for (const order of recentOrdersData) {
						// Get customer name from profiles table
						const { data: userData } = await supabase
							.from("profiles")
							.select("name")
							.eq("id", order.user_id)
							.single();

						recentOrders.push({
							id: order.id,
							created_at: order.created_at,
							amount: parseFloat(order.amount.toString()),
							customer_name: userData?.name || "Unknown Customer",
							status: order.status,
						});
					}
				}

				// Fetch most viewed products
				const { data: mostViewedData, error: mostViewedError } =
					await supabase
						.from("products")
						.select(
							`
            id, 
            name, 
            price, 
            total_views,
            product_images (image_url, is_primary)
          `
						)
						.eq("supplier_id", user.id)
						.order("total_views", { ascending: false })
						.limit(5);

				if (mostViewedError) throw mostViewedError;

				const mostViewedProducts =
					mostViewedData?.map((product) => {
						const primaryImage =
							product.product_images?.find(
								(img) => img.is_primary
							)?.image_url ||
							product.product_images?.[0]?.image_url;

						return {
							id: product.id,
							name: product.name,
							price: parseFloat(product.price.toString()),
							image_url: primaryImage,
							total_views: product.total_views || 0,
							total_ordered: 0, // Will be populated in the next query
						};
					}) || [];

				// Fetch top selling products using our database function
				const { data: topSellingData, error: topSellingError } =
					await supabase.rpc("get_top_selling_products", {
						supplier_id: user.id,
					});

				console.log("Top selling products:", topSellingData);

				let topSellingProducts: Product[] = [];

				if (topSellingError) {
					console.error(
						"Error fetching top selling products:",
						topSellingError
					);

					// If the function call fails, we'll use an empty array
					topSellingProducts = [];
				} else {
					// Process the data from our custom function
					topSellingProducts = topSellingData.map((item: any) => ({
						id: item.product_id,
						name: item.product_name,
						price: parseFloat(item.price.toString()),
						image_url: item.image_url,
						total_ordered: item.total_quantity,
						total_views:
							mostViewedProducts.find(
								(p) => p.id === item.product_id
							)?.total_views || 0,
					}));
				}

				// Calculate monthly sales for chart
				const monthNames = [
					"Jan",
					"Feb",
					"Mar",
					"Apr",
					"May",
					"Jun",
					"Jul",
					"Aug",
					"Sep",
					"Oct",
					"Nov",
					"Dec",
				];
				const now = new Date();
				const monthlySales = [];

				for (let i = 5; i >= 0; i--) {
					const month = new Date(
						now.getFullYear(),
						now.getMonth() - i,
						1
					);
					const monthName = monthNames[month.getMonth()];

					// Filter orders for this month
					const firstDay = new Date(
						month.getFullYear(),
						month.getMonth(),
						1
					).toISOString();
					const lastDay = new Date(
						month.getFullYear(),
						month.getMonth() + 1,
						0
					).toISOString();

					const { data: monthlyOrderData } = await supabase
						.from("orders")
						.select("amount")
						.eq("supplier_id", user.id)
						.gte("created_at", firstDay)
						.lte("created_at", lastDay);

					const monthTotal =
						monthlyOrderData?.reduce(
							(sum, order) =>
								sum + parseFloat(order.amount.toString()),
							0
						) || 0;

					monthlySales.push({
						name: monthName,
						value: monthTotal,
					});
				}

				setStats({
					totalOrders,
					totalEarnings,
					recentOrders,
					mostViewedProducts,
					topSellingProducts,
					monthlySales,
				});
			} catch (error) {
				console.error("Error fetching dashboard data:", error);
			} finally {
				setIsLoading(false);
			}
		}

		fetchDashboardData();
	}, [user]);

	if (isLoading) {
		return (
			<div className="space-y-6">
				<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
					{Array(4)
						.fill(0)
						.map((_, i) => (
							<Skeleton key={i} className="h-32" />
						))}
				</div>
				<Skeleton className="h-64" />
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					<Skeleton className="h-80" />
					<Skeleton className="h-80" />
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-2xl font-semibold tracking-tight">
					Supplier Dashboard
				</h1>
				<p className="text-gray-500">
					Monitor your business performance and sales analytics.
				</p>
			</div>

			{/* Stats Cards */}
			<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Total Orders
						</CardTitle>
						<ShoppingCart className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							{stats.totalOrders}
						</div>
						<p className="text-xs text-muted-foreground">
							Total orders placed
						</p>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Total Revenue
						</CardTitle>
						<DollarSign className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							{formatCurrency(stats.totalEarnings)}
						</div>
						<p className="text-xs text-muted-foreground">
							Total earnings from sales
						</p>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Top Product
						</CardTitle>
						<TrendingUp className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold truncate">
							{stats.topSellingProducts.length > 0
								? stats.topSellingProducts[0].name
								: "No sales yet"}
						</div>
						<p className="text-xs text-muted-foreground">
							Most sold product
						</p>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Most Viewed
						</CardTitle>
						<Package className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold truncate">
							{stats.mostViewedProducts.length > 0
								? stats.mostViewedProducts[0].name
								: "No views yet"}
						</div>
						<p className="text-xs text-muted-foreground">
							Product with most views
						</p>
					</CardContent>
				</Card>
			</div>

			{/* Revenue Chart */}
			<Card>
				<CardHeader>
					<CardTitle>Monthly Revenue</CardTitle>
				</CardHeader>
				<CardContent className="pt-2">
					<div className="h-[300px]">
						{stats.monthlySales.length > 0 ? (
							<ResponsiveContainer width="100%" height="100%">
								<AreaChart
									data={stats.monthlySales}
									margin={{
										top: 10,
										right: 30,
										left: 0,
										bottom: 0,
									}}>
									<defs>
										<linearGradient
											id="colorUv"
											x1="0"
											y1="0"
											x2="0"
											y2="1">
											<stop
												offset="5%"
												stopColor="#8884d8"
												stopOpacity={0.8}
											/>
											<stop
												offset="95%"
												stopColor="#8884d8"
												stopOpacity={0}
											/>
										</linearGradient>
									</defs>
									<CartesianGrid strokeDasharray="3 3" />
									<XAxis dataKey="name" />
									<YAxis />
									<Tooltip
										formatter={(value) =>
											formatCurrency(value as number)
										}
									/>
									<Area
										type="monotone"
										dataKey="value"
										stroke="#8884d8"
										fillOpacity={1}
										fill="url(#colorUv)"
									/>
								</AreaChart>
							</ResponsiveContainer>
						) : (
							<div className="flex items-center justify-center h-full text-gray-400">
								No revenue data available yet
							</div>
						)}
					</div>
				</CardContent>
			</Card>

			{/* Recent Orders and Product Stats */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Recent Orders */}
				<Card>
					<CardHeader>
						<CardTitle>Recent Orders</CardTitle>
					</CardHeader>
					<CardContent>
						{stats.recentOrders.length > 0 ? (
							<div className="space-y-4">
								{stats.recentOrders.map((order) => (
									<div
										key={order.id}
										className="flex justify-between items-center border-b pb-4 last:border-b-0 last:pb-0">
										<div>
											<div className="font-medium">
												{order.customer_name}
											</div>
											<div className="text-sm text-gray-500">
												{new Date(
													order.created_at
												).toLocaleDateString()}
											</div>
										</div>
										<div className="text-right">
											<div className="font-medium">
												{formatCurrency(order.amount)}
											</div>
											<Badge
												variant={
													order.status === "delivered"
														? "default"
														: order.status ===
														  "shipped"
														? "outline"
														: order.status ===
														  "processing"
														? "secondary"
														: order.status ===
														  "cancelled"
														? "destructive"
														: "default"
												}>
												{order.status}
											</Badge>
										</div>
									</div>
								))}
							</div>
						) : (
							<div className="text-center py-8 text-gray-400">
								No orders received yet
							</div>
						)}
					</CardContent>
				</Card>

				{/* Top Products */}
				<Card>
					<CardHeader>
						<CardTitle>Top Selling Products</CardTitle>
					</CardHeader>
					<CardContent>
						{stats.topSellingProducts.length > 0 ? (
							<div className="h-[300px]">
								<ResponsiveContainer width="100%" height="100%">
									<BarChart
										data={stats.topSellingProducts.map(
											(product) => ({
												name:
													product.name.length > 20
														? product.name.substring(
																0,
																20
														  ) + "..."
														: product.name,
												quantity: product.total_ordered,
												views: product.total_views,
											})
										)}
										margin={{
											top: 20,
											right: 30,
											left: 20,
											bottom: 60,
										}}>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis
											dataKey="name"
											angle={-45}
											textAnchor="end"
											height={60}
											interval={0}
										/>
										<YAxis />
										<Tooltip />
										<Legend />
										<Bar
											dataKey="quantity"
											name="Units Sold"
											fill="#8884d8"
										/>
										<Bar
											dataKey="views"
											name="Views"
											fill="#82ca9d"
										/>
									</BarChart>
								</ResponsiveContainer>
							</div>
						) : (
							<div className="text-center py-8 text-gray-400">
								No product sales data available yet
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
